/**
 * <malvros-jersey-picker>
 * Pick several variants (club x size) of one product with a quantity each,
 * then add them all to the cart in one request.
 *
 * All user-facing text comes from data-t-* attributes rendered by
 * snippets/malvros-jersey-picker.liquid, so translations live in one place.
 */
(() => {
  if (customElements.get('malvros-jersey-picker')) return;

  const fill = (template, values) =>
    template.replace(/\{(\w+)\}/g, (_, key) => (key in values ? values[key] : ''));

  class MalvrosJerseyPicker extends HTMLElement {
    connectedCallback() {
      this.cells = [...this.querySelectorAll('[data-variant-cell]')];
      this.rows = [...this.querySelectorAll('[data-group-row]')];
      this.addButton = this.querySelector('[data-add]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.spinner = this.querySelector('[data-spinner]');
      this.summary = this.querySelector('[data-summary]');
      this.errorBox = this.querySelector('[data-error]');

      this.limit = Number.parseInt(this.dataset.orderLimit, 10) || 20;
      this.isSubmitting = false;

      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'MAD',
        });
      } catch (error) {
        this.money = null;
      }

      this.cells.forEach((cell) => {
        const input = this.inputOf(cell);
        cell.querySelector('[data-minus]')?.addEventListener('click', () => this.setQuantity(cell, this.quantityOf(cell) - 1));
        cell.querySelector('[data-plus]')?.addEventListener('click', () => this.setQuantity(cell, this.quantityOf(cell) + 1));
        // 'change' fires on blur/enter, and always before a click on the Add button
        input?.addEventListener('change', () => this.setQuantity(cell, input.value));
      });

      this.addButton?.addEventListener('click', () => this.addToCart());

      // Restoring from the back/forward cache: clear the stuck "adding" state
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.setSubmitting(false);
      });

      this.render();
    }

    inputOf(cell) {
      return cell.querySelector('[data-input]');
    }

    quantityOf(cell) {
      const quantity = Number.parseInt(this.inputOf(cell)?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    }

    maxOf(cell) {
      const max = Number.parseInt(cell.dataset.max, 10);
      return Number.isFinite(max) && max >= 0 ? max : this.limit;
    }

    totalCount() {
      return this.cells.reduce((sum, cell) => sum + this.quantityOf(cell), 0);
    }

    totalPrice() {
      // data-price is in minor units (cents)
      return this.cells.reduce((sum, cell) => sum + this.quantityOf(cell) * (Number(cell.dataset.price) || 0), 0) / 100;
    }

    selectedItems() {
      return this.cells
        .map((cell) => ({ id: Number.parseInt(cell.dataset.variantId, 10), quantity: this.quantityOf(cell) }))
        .filter((item) => Number.isFinite(item.id) && item.quantity > 0);
    }

    setQuantity(cell, value) {
      const input = this.inputOf(cell);
      if (!input || input.disabled) return;

      const requested = Math.max(Number.parseInt(value, 10) || 0, 0);
      const elsewhere = this.totalCount() - this.quantityOf(cell);
      const allowed = Math.max(Math.min(this.maxOf(cell), this.limit - elsewhere), 0);
      const next = Math.min(requested, allowed);

      if (String(next) !== input.value) input.value = String(next);

      if (requested > next && requested > 0) {
        this.showError(this.dataset.tLimit);
      } else {
        this.hideError();
      }
      this.render();
    }

    render() {
      const count = this.totalCount();
      const locked = this.isSubmitting;

      this.cells.forEach((cell) => {
        const quantity = this.quantityOf(cell);
        const canAdd = cell.dataset.available === 'true' && quantity < this.maxOf(cell) && count < this.limit;
        cell.classList.toggle('has-selection', quantity > 0);
        const minus = cell.querySelector('[data-minus]');
        const plus = cell.querySelector('[data-plus]');
        if (minus) minus.disabled = locked || quantity <= 0;
        if (plus) plus.disabled = locked || !canAdd;
      });

      this.rows.forEach((row) => {
        row.classList.toggle('has-selection', row.querySelector('.has-selection') !== null);
      });

      if (this.summary) {
        if (count === 0) {
          this.summary.textContent = fill(this.dataset.tNone, { max: this.limit });
        } else {
          const total = this.totalPrice();
          let text = fill(this.dataset.tSelected, { count, max: this.limit });
          if (total > 0) {
            const formatted = this.money ? this.money.format(total) : total.toFixed(2);
            text += ` · ${fill(this.dataset.tTotal, { total: formatted })}`;
          }
          this.summary.textContent = text;
        }
      }

      if (this.addLabel && !this.isSubmitting) {
        this.addLabel.textContent = count === 0 ? this.dataset.tEmpty : fill(this.dataset.tAdd, { count });
      }
      if (this.addButton) this.addButton.disabled = locked || count === 0;
    }

    setSubmitting(value) {
      this.isSubmitting = value;
      this.spinner?.classList.toggle('hidden', !value);
      if (this.addLabel && value) this.addLabel.textContent = this.dataset.tLoading;
      this.render();
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message || this.dataset.tError;
      this.errorBox.hidden = false;
    }

    hideError() {
      if (this.errorBox) this.errorBox.hidden = true;
    }

    async addToCart() {
      const items = this.selectedItems();
      if (items.length === 0 || this.isSubmitting) return;
      if (this.totalCount() > this.limit) {
        this.showError(this.dataset.tLimit);
        return;
      }

      this.hideError();
      this.setSubmitting(true);

      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || this.dataset.tError);

        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.showError(error instanceof Error ? error.message : this.dataset.tError);
        this.setSubmitting(false);
      }
    }
  }

  customElements.define('malvros-jersey-picker', MalvrosJerseyPicker);
})();
